// app/servicos/desenvolvimento-web/DesenvolvimentoWebPage.tsx
"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';
import ServiceButton from '../../../components/ui/ServiceButton';

const DesenvolvimentoWebPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Para o processo de desenvolvimento
  const processoDesenvolvimento = [
    {
      step: 1,
      title: 'Briefing Detalhado',
      description: 'Entendemos profundamente seu negócio, público e objetivos',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      step: 2,
      title: 'Planejamento Estratégico',
      description: 'Definimos arquitetura, wireframes e palavras-chave',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      step: 3,
      title: 'Design Personalizado',
      description: 'Criamos interfaces únicas alinhadas à sua identidade',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      step: 4,
      title: 'Desenvolvimento Frontend/Backend',
      description: 'Codificação com as melhores práticas',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    },
    {
      step: 5,
      title: 'Otimização para SEO',
      description: 'Implementamos técnicas avançadas para ranqueamento',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
    },
    {
      step: 6,
      title: 'Testes e Refinamentos',
      description: 'Garantimos desempenho em todos os dispositivos',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      )
    },
    {
      step: 7,
      title: 'Lançamento e Suporte',
      description: 'Acompanhamento contínuo após a entrega',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    }
  ];

  // Comparativo entre personalizado e templates
  const comparativoTabela = [
    {
      criterio: 'Exclusividade visual',
      personalizado: 'Design único e exclusivo alinhado à sua marca',
      template: 'Layouts predefinidos com limitada personalização'
    },
    {
      criterio: 'Velocidade de carregamento',
      personalizado: 'Código otimizado para máximo desempenho',
      template: 'Excesso de código desnecessário que prejudica a velocidade'
    },
    {
      criterio: 'Otimização para SEO',
      personalizado: 'Estrutura planejada para alto ranqueamento em buscas',
      template: 'Configurações básicas de SEO com limitações técnicas'
    },
    {
      criterio: 'Funcionalidades',
      personalizado: 'Recursos desenvolvidos sob medida para suas necessidades',
      template: 'Funcionalidades genéricas com pouca flexibilidade'
    },
    {
      criterio: 'Segurança',
      personalizado: 'Proteções personalizadas contra vulnerabilidades',
      template: 'Alvo frequente de ataques por usar código conhecido'
    },
    {
      criterio: 'Escalabilidade',
      personalizado: 'Crescimento planejado de acordo com evolução do negócio',
      template: 'Limitações técnicas para expansões futuras'
    }
  ];

  // Tecnologias utilizadas
  const tecnologias = [
    {
      categoria: 'Frontend Moderno',
      techs: ['React', 'Next.js', 'Vue.js', 'Framer Motion'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      categoria: 'Backend Robusto',
      techs: ['Node.js', 'PHP', 'Python', 'GraphQL'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      )
    },
    {
      categoria: 'Bancos de Dados',
      techs: ['MySQL', 'MongoDB', 'Firebase', 'PostgreSQL'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    },
    {
      categoria: 'Otimização e Desempenho',
      techs: ['Lighthouse', 'WebP', 'Lazy Loading', 'Code Splitting'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      categoria: 'Segurança Avançada',
      techs: ['HTTPS', 'WAF', 'Proteção DDoS', 'OWASP Top 10'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    }
  ];

  // Cases de sucesso (portfólio)
  const casesSucesso = [
    {
      nome: 'E-commerce de Moda Sustentável',
      desafio: 'Criar uma plataforma de vendas para uma marca de moda sustentável com experiência de compra fluida e alto desempenho.',
      solucao: 'Desenvolvemos um e-commerce personalizado com filtros avançados, integração com gateway de pagamento e sistema de gestão de estoque.',
      resultados: 'Aumento de 85% na taxa de conversão e redução de 40% na taxa de abandono de carrinho.',
      imagem: '/images/cases/ecommerce-case.jpg'
    },
    {
      nome: 'Portal Corporativo para Escritório de Advocacia',
      desafio: 'Criar um site institucional que transmitisse autoridade e oferecesse área de cliente para acompanhamento de processos.',
      solucao: 'Desenvolvimento de portal elegante com área restrita, sistema de agendamento e blog otimizado para SEO jurídico.',
      resultados: 'Primeiro lugar em buscas locais por termos-chave e aumento de 63% nos leads qualificados.',
      imagem: '/images/cases/advocacia-case.jpg'
    },
    {
      nome: 'Plataforma de Cursos Online',
      desafio: 'Criar um sistema completo de EAD com vídeo-aulas, materiais complementares e certificação automática.',
      solucao: 'Plataforma personalizada com player de vídeo otimizado, sistema de progressão de aulas e dashboard do aluno.',
      resultados: 'Redução de 70% na taxa de abandono e aumento de 45% no tempo médio de permanência nas aulas.',
      imagem: '/images/cases/cursos-case.jpg'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      pergunta: 'Quanto tempo leva para desenvolver um website personalizado?',
      resposta: 'O tempo de desenvolvimento varia conforme a complexidade do projeto. Um site institucional pode levar de 3 a 5 semanas, enquanto um e-commerce ou plataforma avançada pode levar de 8 a 12 semanas. Fornecemos um cronograma detalhado no início do projeto.'
    },
    {
      pergunta: 'Qual a diferença entre um site personalizado e um feito com WordPress?',
      resposta: 'Um site WordPress usa um CMS padronizado com temas e plugins genéricos, enquanto um site personalizado é desenvolvido do zero especificamente para suas necessidades. Sites personalizados oferecem maior exclusividade, segurança, desempenho e flexibilidade para implementar qualquer funcionalidade necessária.'
    },
    {
      pergunta: 'Como um site personalizado pode melhorar o SEO da minha empresa?',
      resposta: 'Sites personalizados permitem implementação de melhores práticas de SEO em nível de código, com estrutura semântica otimizada, velocidade de carregamento superior e experiência do usuário aprimorada. Podemos focar em fatores específicos de SEO para seu nicho de mercado, resultando em melhor ranqueamento nos motores de busca.'
    },
    {
      pergunta: 'Vocês oferecem manutenção após a entrega do website?',
      resposta: 'Sim, oferecemos planos de manutenção mensal que incluem atualizações de segurança, backups, monitoramento de desempenho, pequenas alterações de conteúdo e suporte técnico. Isso garante que seu site permaneça seguro, atualizado e funcionando perfeitamente.'
    },
    {
      pergunta: 'É possível integrar sistemas e APIs externas ao meu website?',
      resposta: 'Absolutamente. Nossos sites personalizados podem integrar com praticamente qualquer sistema ou API externa, como CRMs, ERPs, sistemas de pagamento, marketplaces, redes sociais e ferramentas de automação de marketing. Desenvolvemos integrações sob medida para suas necessidades específicas.'
    },
    {
      pergunta: 'Os websites são responsivos para mobile?',
      resposta: 'Sim, todos nossos websites são desenvolvidos com abordagem mobile-first, garantindo experiência perfeita em todos os dispositivos e tamanhos de tela. Testamos extensivamente em múltiplos dispositivos e navegadores para assegurar compatibilidade total.'
    }
  ];

  // Schema.org JSON-LD para SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Desenvolvimento Web",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Web Imports",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua Tapuru, 25, Alpha Mall / Loja 26",
        "addressLocality": "Campinas",
        "addressRegion": "SP",
        "postalCode": "13098-322",
        "addressCountry": "BR"
      }
    },
    "areaServed": "Campinas",
    "description": "Criamos websites exclusivos e personalizados com foco em SEO, design moderno e segurança avançada, sem utilizar templates ou módulos genéricos.",
    "offers": {
      "@type": "Offer",
      "price": "A partir de R$ 4.990",
      "priceCurrency": "BRL"
    }
  };

  return (
    <>
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <Link 
                  href="/servicos" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Serviços
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Desenvolvimento Web
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#3B82F6" : "#60A5FA"} 
              fillOpacity="0.2" 
            />
          </svg>
          
          <svg 
            className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.2" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={fadeIn}
              className="lg:w-1/2"
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Desenvolvimento Web Personalizado
              </div>
              <h1 className={`text-3xl lg:text-4xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Websites Únicos e <span className="text-blue-600">Exclusivos</span> que Convertem
              </h1>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Transformamos sua visão em uma presença digital única, com foco total em resultados.
                Sem templates, sem limitações. Apenas a solução perfeita para o seu negócio.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <ServiceButton 
                  text="Solicitar Orçamento"
                  href="#contato"
                  className="text-white font-bold"
                />
                <ServiceButton 
                  text="Ver Projetos"
                  href="#portfolio"
                  isPrimary={false}
                  className="font-bold"
                />
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className={`w-full h-80 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'
                } flex items-center justify-center overflow-hidden`}>
                  <Image
                    src="/images/web-development-illustration.svg"
                    alt="Desenvolvimento Web Personalizado"
                    width={500}
                    height={400}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
                
                {/* Estatística flutuante */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -left-6 p-6 rounded-lg shadow-xl ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                  }`}
                >
                  <p className="text-blue-600 font-bold text-4xl">+40%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    mais conversões com sites personalizados
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processo de Desenvolvimento Section */}
      <section 
        id="processo" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Metodologia
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Como Desenvolvemos seu Website Exclusivo
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Um processo estruturado e transparente para transformar sua visão em realidade digital
            </p>
          </div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {processoDesenvolvimento.map((passo) => (
              <motion.div
                key={passo.step}
                variants={fadeIn}
                className={`relative ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg`}
              >
                <div className={`flex items-center mb-3`}>
                  <div className={`w-8 h-8 rounded-lg ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-br from-blue-900 to-blue-800 text-blue-300' 
                      : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                    } flex items-center justify-center mr-2 font-medium`}>
                    {passo.step}
                  </div>
                  <h3 className={`text-lg font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{passo.title}</h3>
                </div>
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } pl-10`}>
                  {passo.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Comparativo Section */}
      <section 
        id="diferencial" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Diferencial
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Por que Escolher um Website Personalizado?
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Entenda as vantagens de um desenvolvimento sob medida em comparação com soluções prontas
            </p>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className={`overflow-hidden rounded-xl shadow-xl border ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
                  }`}>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Critério</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'bg-blue-900/50 text-blue-100' : 'bg-blue-50 text-blue-900'
                    }`}>Site Personalizado</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>Template/WordPress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {comparativoTabela.map((item, index) => (
                    <tr key={index} className={`${
                      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                    } ${index % 2 === 1 ? theme === 'dark' ? 'bg-gray-900/70' : 'bg-gray-50' : ''}`}>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      } font-medium`}>{item.criterio}</td>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                      }`}>{item.personalizado}</td>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>{item.template}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tecnologias Section */}
      <section 
        id="tecnologias"
        className={`py-8 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-6">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-2`}>
              Tecnologias
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Ferramentas Utilizadas em Nosso Processo
            </h2>
            <p className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Soluções modernas e eficientes para cada necessidade específica
            </p>
          </div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-5 lg:grid-cols-5 gap-3"
          >
            {tecnologias.map((tech, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className={`${
                  theme === 'dark' ? 'bg-gray-900 border border-gray-700' : 'bg-white shadow'
                } rounded-xl p-3`}
              >                
                <h3 className={`text-sm font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}>{tech.categoria}</h3>
                
                <div className="flex flex-wrap gap-1 mt-2">
                  {tech.techs.map((item, i) => (
                    <span
                      key={i}
                      className={`px-2 py-1 rounded-full text-xs ${
                        theme === 'dark' 
                          ? 'bg-gray-800 text-blue-300 border border-blue-900' 
                          : 'bg-blue-50 text-blue-800'
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfólio Section */}
      <section 
        id="portfolio"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Projetos Realizados
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Cases de Sucesso em Desenvolvimento Web
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Conheça alguns dos projetos que desenvolvemos e os resultados que alcançamos
            </p>
          </div>
          
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
            {casesSucesso.map((caso, index) => (
              <motion.div
                key={caso.nome}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
                } rounded-xl overflow-hidden h-full flex flex-col`}
              >
                <div className="h-40 overflow-hidden">
                  <Image 
                    src={caso.imagem} 
                    alt={caso.nome}
                    width={500}
                    height={300}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className={`text-xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-3`}>{caso.nome}</h3>
                  
                  <div className="space-y-3 mb-3 flex-1">
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                      } mb-1`}>Desafio:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{caso.desafio}</p>
                    </div>
                    
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                      } mb-1`}>Solução:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{caso.solucao}</p>
                    </div>
                    
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-green-300' : 'text-green-700'
                      } mb-1`}>Resultados:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm font-medium`}>{caso.resultados}</p>
                    </div>
                  </div>
                  
                  <motion.a 
                    href={`/case/${caso.nome.toLowerCase().replace(/\s+/g, '-')}`}
                    whileHover={{ x: 5 }}
                    className={`inline-flex items-center text-sm font-medium ${
                      theme === 'dark' 
                        ? 'text-blue-400 hover:text-blue-300' 
                        : 'text-blue-700 hover:text-blue-800'
                    }`}
                  >
                    <span>Ver detalhes do projeto</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <motion.a 
              href="/portfolio?categoria=desenvolvimento-web" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-extrabold text-lg tracking-wide ${
                theme === 'dark' 
                  ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } shadow-lg hover:shadow-xl transition-all`}
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                color: '#ffffff'
              }}
            >
              <span>Ver Todos os Projetos</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Perguntas Frequentes
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Dúvidas comuns sobre nossos serviços de desenvolvimento web personalizado.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto"
               itemScope
               itemType="https://schema.org/FAQPage">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="contato"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Vamos Conversar
              </div>
              <h2 className={`text-2xl md:text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-3`}>
                Vamos Criar o Website Ideal para seu Negócio
              </h2>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Preencha o formulário ao lado e nossa equipe entrará em contato em até 24 horas para entender melhor seu projeto.
              </p>
              
              <div className="space-y-4">
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-3 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="tel:+551932060706" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      (19) 3206-0706
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'
                  } flex items-center justify-center mr-3 ${
                    theme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a 
                      href="https://wa.me/5519994799400" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${
                        theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-3 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@webimports.com.br" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className={`${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
              } rounded-xl p-5`}
            >
              <h3 className={`text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-6`}>
                Solicite um orçamento personalizado
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DesenvolvimentoWebPage;